/**
 * O próximo passo da bancada para um ensaio — uma regra só (onda 373).
 *
 * «Ao acessar o QR code de uma amostra, aparece a lista de ensaios
 *  cadastrados nela. Preciso que tenha um botão de fácil acesso,
 *  principalmente no celular, para que os operadores executem o ensaio.
 *  Assim incentiva o uso do QR code e facilita a execução.» — Humberto,
 *  03/11/2026 (aprovou a opção A).
 *
 * ── Por que este arquivo existe ──────────────────────────────────────
 *
 * A regra do botão («Lançar», «Corrigir», «Verificar» ou nenhum) morava na
 * Fila do laboratório (`telas/Fila.tsx`, ondas 270 e 309). Com a tela da
 * amostra — a que o QR abre — oferecendo o mesmo botão, a regra passou a ter
 * dois clientes, e duas cópias seriam duas respostas no dia em que uma delas
 * mudasse: a Fila diria «Lançar» e a prateleira, nada. Aqui ficam as funções
 * PURAS, sem React e sem sessão; quem chama diz o que a pessoa pode
 * (`ensaio.lancar`, `ensaio.verificar`). O servidor continua barrando no
 * gravar — a tela só não oferece o que não vai dar certo.
 *
 * O tipo de entrada é o mínimo: o que a `vw_ensaio` entrega e a regra lê. A
 * Fila passa o ensaio dela inteiro; a amostra, o que o `GET
 * /amostras/codigo/:codigo` devolve.
 */

/** O que a regra precisa saber de um ensaio — colunas da `vw_ensaio`. */
export interface EnsaioParaAcao {
  id: string;
  status: string;
  /** Fora de aprovado, cancelado e substituído. */
  aberto: boolean;
  /** Onde se lança este método. Nulo = ainda não há ficha (0076). */
  telas_lancamento: string[] | null;
  /** Por que este ensaio ainda não pode começar — ou nulo, se pode (0078). */
  impedimento_amostra: string | null;
}

/** Uma ação da bancada: o texto do botão e para onde ele leva. */
export interface AcaoDoEnsaio {
  texto: string;
  para: string;
}

/**
 * Onde se lança cada ensaio — pergunta ao BANCO, não ao nome.
 *
 * ── A tabela de prefixos que morava aqui, e o que ela custou ────────
 *
 * Isto era uma lista de prefixos em JavaScript: `COMP` → compactação,
 * `MES` → massa específica, `ISC` → isc. Funcionou enquanto os códigos
 * eram do próprio laboratório.
 *
 * A onda 35 trouxe 55 códigos da lista de preços da Testesolo, e a
 * regra passou a errar **dos dois lados**:
 *
 *   · `COMP.S` (compressão simples), `COMP.NC` (compressão não
 *     confinada) e `COMP.TRI.I` (molde tripartido) abriam a ficha do
 *     PROCTOR — três ensaios diferentes numa tela que não serve a
 *     nenhum deles;
 *   · `CBR.1`, `CBR.5` e `M.ESP` não abriam ficha nenhuma, tendo ficha:
 *     o prefixo esperava `ISC` e `MES`.
 *
 * O comentário que estava aqui já avisava — «errar para mais leva o
 * técnico a uma tela que não serve ao método dele» — e o defeito
 * chegou por baixo, quando o catálogo mudou e a lista não.
 *
 * ── Por que a correção não é uma lista melhor ───────────────────────
 *
 * Porque adivinhar pelo NOME é a mesma classe de erro que a migração
 * `0070` documentou: procurar pelo nome é procurar pelo que já se
 * espera encontrar. A tela de lançamento é propriedade do MÉTODO, e
 * método é cadastro.
 *
 * Desde a `0076` a `vw_ensaio` entrega `telas_lancamento` pronta, e a
 * regra inteira mora numa função do banco — inclusive a compatibilidade
 * com os códigos antigos. Aqui só se lê (D-29).
 *
 * ── E são DUAS em um método ────────────────────────────────────────
 *
 * O `LA` — Limites de Atterberg — é um item de medição com dois
 * lançamentos: LL e LP. «Um item com 2 lançamentos», decisão do Humberto
 * em 30/08/2026. Um ensaio só carrega as duas fichas, o laudo encontra o
 * par e calcula o IP, e a medição conta um item — mas a bancada faz dois
 * procedimentos, e a linha precisa de dois links.
 */
export const telasDe = (e: Pick<EnsaioParaAcao, 'telas_lancamento'>): string[] =>
  e.telas_lancamento ?? [];

/**
 * Como cada ficha se chama para quem vai clicar.
 *
 * Só entra aqui a ficha que divide linha com outra: com um link só, a
 * palavra «Lançar» já diz tudo, e qualificá-la seria ruído.
 */
export const NOME_DA_FICHA: Record<string, string> = {
  'limite-liquidez': 'LL',
  'limite-plasticidade': 'LP',
};

/** O nome curto da ficha, ou o próprio código quando ela não divide linha. */
export const nomeDaFicha = (tela: string): string => NOME_DA_FICHA[tela] ?? tela;

/** A ação da linha: o próximo passo da bancada, e só ele. */
export function acaoDe(e: EnsaioParaAcao): AcaoDoEnsaio | null {
  const telas = telasDe(e);
  if (!e.aberto) return null;
  if (e.impedimento_amostra) return null;
  if (e.status === 'aguardando_verificacao') return { texto: 'Verificar', para: `/ensaio/${e.id}/situacao` };
  if (telas.length === 0) return null;
  if (e.status === 'devolvido_para_correcao') return { texto: 'Corrigir', para: `/ensaio/${e.id}/${telas[0]}` };
  return { texto: 'Lançar', para: `/ensaio/${e.id}/${telas[0]}` };
}

/** O LA na bancada: um ensaio, duas fichas, dois botões (30/08). */
export function temDuasFichas(e: EnsaioParaAcao): boolean {
  const acao = acaoDe(e);
  return !!acao && telasDe(e).length > 1 && acao.texto === 'Lançar';
}

/** O que a pessoa pode fazer — quem chama lê da sessão (`pode(...)`). */
export interface PodeNaBancada {
  /** `ensaio.lancar`: lançar e corrigir. */
  lancar: boolean;
  /** `ensaio.verificar`: a mesma permissão que a API exige para verificar. */
  verificar: boolean;
}

/**
 * Os botões de um ensaio, na ordem em que aparecem.
 *
 * Um, em geral; dois no LA («Lançar LL», «Lançar LP» — o primeiro é o
 * primário do cartão, P-UI-10); nenhum quando a regra não dá ação ou a
 * pessoa não tem a permissão daquela ação.
 */
export function botoesDoEnsaio(e: EnsaioParaAcao, pode: PodeNaBancada): AcaoDoEnsaio[] {
  const acao = acaoDe(e);
  if (!acao) return [];
  if (acao.texto === 'Verificar') return pode.verificar ? [acao] : [];
  if (!pode.lancar) return [];
  if (temDuasFichas(e)) {
    return telasDe(e).map((t) => ({ texto: `Lançar ${nomeDaFicha(t)}`, para: `/ensaio/${e.id}/${t}` }));
  }
  return [acao];
}

/**
 * Por que um ensaio aberto não tem ação — ou nulo, se tem (ou se está
 * fechado). O impedimento é o texto que o banco escreveu (0078); sem ficha,
 * a frase de sempre da Fila.
 */
export function motivoSemAcao(e: EnsaioParaAcao): string | null {
  if (!e.aberto) return null;
  if (e.impedimento_amostra) return e.impedimento_amostra;
  if (acaoDe(e)) return null;
  return 'ficha de lançamento ainda não implementada';
}
